import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { XMarkIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { youtubeApi } from '@/services/youtubeApi';
import { socialApi, SocialPlatform } from '@/services/socialApi';
import publishingApi, { PublishingPlatform } from '@/services/publishingApi';
import { IGeneratedClip } from '@/services/clipGenerationApi';

interface BulkScheduleModalProps {
  clipGenerationJobId: string;
  platform: PublishingPlatform;
  platformLabel: string;
  clips: IGeneratedClip[];
  onClose: () => void;
  onScheduled: () => void;
}

const CADENCE_UNITS: { value: 'hours' | 'days'; label: string }[] = [
  { value: 'hours', label: 'hours' },
  { value: 'days', label: 'days' },
];

export default function BulkScheduleModal({ clipGenerationJobId, platform, platformLabel, clips, onClose, onScheduled }: BulkScheduleModalProps) {
  const isYouTube = platform === 'youtube';

  const { data: youtubeAccountsData } = useQuery({
    queryKey: ['youtube-accounts'],
    queryFn: () => youtubeApi.getAccounts(),
    enabled: isYouTube,
  });
  const { data: socialAccountsData } = useQuery({
    queryKey: ['social-accounts', platform],
    queryFn: () => socialApi.getAccounts(platform as SocialPlatform),
    enabled: !isYouTube,
  });
  const accounts = isYouTube ? youtubeAccountsData?.accounts || [] : socialAccountsData?.accounts || [];

  const [accountId, setAccountId] = useState('');
  const [cadenceAmount, setCadenceAmount] = useState(1);
  const [cadenceUnit, setCadenceUnit] = useState<'hours' | 'days'>('days');
  const [startAt, setStartAt] = useState(() => {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    return d.toISOString().slice(0, 16);
  });
  const [titleTemplate, setTitleTemplate] = useState('{clipTitle}');
  const [privacyStatus, setPrivacyStatus] = useState<'private' | 'unlisted' | 'public'>('private');
  const [selfDeclaredMadeForKids, setSelfDeclaredMadeForKids] = useState<boolean | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const cadenceMs = useMemo(() => cadenceAmount * (cadenceUnit === 'days' ? 86400000 : 3600000), [cadenceAmount, cadenceUnit]);
  const preview = useMemo(() => {
    const start = new Date(startAt);
    if (Number.isNaN(start.getTime())) return [];
    return clips.map((clip, i) => ({
      clip,
      scheduledFor: new Date(start.getTime() + i * cadenceMs),
    }));
  }, [clips, startAt, cadenceMs]);

  const canSubmit = !!accountId && (!isYouTube || selfDeclaredMadeForKids !== null) && preview.length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const result = await publishingApi.bulkSchedule({
        platform,
        accountId,
        clipRefs: clips.map((c) => ({ jobId: clipGenerationJobId, clipId: c.clipId })),
        cadence: { unit: cadenceUnit, amount: cadenceAmount },
        startAt: new Date(startAt).toISOString(),
        ...(isYouTube
          ? { titleTemplate, privacyStatus, selfDeclaredMadeForKids: !!selfDeclaredMadeForKids }
          : { captionTemplate: titleTemplate }),
      });
      if (result.created.length > 0) {
        toast.success(`Scheduled ${result.created.length} of ${clips.length} clips`);
      }
      if (result.failed.length > 0) {
        toast.error(`${result.failed.length} clip(s) could not be scheduled`);
      }
      onScheduled();
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to bulk-schedule clips');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-2xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <CalendarDaysIcon className="h-5 w-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Schedule all {platformLabel} clips</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {clips.length} clip{clips.length === 1 ? '' : 's'} will be scheduled one after another, spaced by the cadence below.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account</label>
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Select an account</option>
              {accounts.map((a: any) => (
                <option key={a._id} value={a._id}>
                  {a.channelTitle || a.accountName}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start date and time</label>
              <input
                type="datetime-local"
                value={startAt}
                min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                onChange={(e) => setStartAt(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cadence</label>
              <div className="flex gap-2">
                <span className="flex items-center text-sm text-gray-600 dark:text-gray-300">Every</span>
                <input
                  type="number"
                  min={1}
                  value={cadenceAmount}
                  onChange={(e) => setCadenceAmount(Math.max(1, Number(e.target.value) || 1))}
                  className="w-16 rounded-md border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <select
                  value={cadenceUnit}
                  onChange={(e) => setCadenceUnit(e.target.value as 'hours' | 'days')}
                  className="flex-1 rounded-md border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {CADENCE_UNITS.map((u) => (
                    <option key={u.value} value={u.value}>
                      {u.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {isYouTube ? 'Title template' : 'Caption template'}
            </label>
            <input
              type="text"
              value={titleTemplate}
              onChange={(e) => setTitleTemplate(e.target.value)}
              placeholder="{clipTitle}"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Use <code>{'{clipTitle}'}</code> and <code>{'{index}'}</code> as placeholders.
            </p>
          </div>

          {isYouTube && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Privacy</label>
                <select
                  value={privacyStatus}
                  onChange={(e) => setPrivacyStatus(e.target.value as any)}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  <option value="private">Private</option>
                  <option value="unlisted">Unlisted</option>
                  <option value="public">Public</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Made for kids *</label>
                <div className="flex gap-2">
                  <label className="flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input type="radio" checked={selfDeclaredMadeForKids === true} onChange={() => setSelfDeclaredMadeForKids(true)} />
                    Yes
                  </label>
                  <label className="flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input type="radio" checked={selfDeclaredMadeForKids === false} onChange={() => setSelfDeclaredMadeForKids(false)} />
                    No
                  </label>
                </div>
              </div>
            </div>
          )}

          {preview.length > 0 && (
            <div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Preview</p>
              <div className="max-h-48 overflow-y-auto space-y-1 rounded-md border border-gray-200 dark:border-gray-700 p-2">
                {preview.map(({ clip, scheduledFor }, i) => (
                  <div key={clip.clipId} className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300 px-1 py-0.5">
                    <span className="truncate pr-2">{i + 1}. {clip.title || 'Untitled clip'}</span>
                    <span className="shrink-0">{scheduledFor.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <button type="button" onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {submitting ? 'Scheduling…' : `Schedule ${clips.length} clips`}
          </button>
        </div>
      </div>
    </div>
  );
}
